<template>
  <div class="highcharts-light user-details-trend-chart chart-container">
    <highcharts :options="chartOptions" ref="chartComponent"></highcharts>
  </div>
</template>

<script>
import {Chart} from 'highcharts-vue';
import Highcharts from 'highcharts';
import addAccessibility from 'highcharts/modules/accessibility';
import noDataToDisplay from 'highcharts/modules/no-data-to-display';

addAccessibility(Highcharts);
noDataToDisplay(Highcharts);

const MAX_HEIGHT = 254;

export default {
  name: 'user-details-trend-chart',
  components: {
    highcharts: Chart
  },
  props: {
    chartSeries: {
      type: Array,
      default: () => []
    },
    categories: {
      type: Array,
      default: () => []
    },
    showLegend: {
      type: Boolean,
      default: false
    },
    clickCallback: {
      type: Function,
      default: undefined
    },
    labelClickCallback: {
      type: Function,
      default: undefined
    },
  },
  data: function() {
    let self = this;
    return {

    }
  },
  computed: {
    chartOptions: function() {
      let self = this;
      let baseUrl = window.location.href.split(/[?#]/)[0]

      return {
        chart: {
          styledMode: true,
          type: 'bar',
          height: calculateHeight(self.categories.length),
          events: {
            render() {
              self.chartRendered();
            }
          }        },
        title: {
          text: undefined
        },
        lang: {
          noData: 'No Data To Display'
        },
        noData: {
          style: {
            fontSize: '14px',
            fontWeight: '400',
            color: '#253035',
            fontFamily: '"Forma DJR UI", Arial, sans-serif'
          }
        },
        legend: {
          enabled: self.showLegend,
          itemStyle: {
            fontSize:'12px',
            color: '#253035',
            fontFamily: '"Forma DJR UI", Arial, sans-serif',
            fontWeight: '600'
          }
        },
        plotOptions: {
          series: {
            cursor: 'pointer',
            pointWidth: 13,
            dataLabels: {
              enabled: true,
              y: -1,
              style: {
                color: '#404E55',
                fontSize: '12px',
                fontFamily: '"Forma DJR UI", Arial, sans-serif',
                fontWeight: '600'
              }
            },
            // TODO: dataSorting property doesn't work with our version of highcharts (6.1.1), but it does with 8.0.0+
            dataSorting: {
              enabled: true
            },
            states: {
              hover: {
                brightness: 0.05
              }
            },
            point: {
              events: {
                click: self.clickCallback
              }
            }
          }
        },
        tooltip: {
          enabled: false
        },
        xAxis: {
          categories: self.categories.map(cat => cat.name),
          tickWidth: 0,
          labels: {
            useHTML: true,
            y: 6,
            align: 'right',
            style: {
              cursor: self.labelClickCallback ? 'pointer' : 'default',
              color: self.labelClickCallback ? 'var(--hover-color)' : '#253035',
              fontSize: '12px',
              fontWeight: '500',
              fontFamily: '"Forma DJR UI", Arial, sans-serif',
              whiteSpace: 'nowrap'
            },
            events: {
              click: function() {
                if (self.labelClickCallback) {
                  self.labelClickCallback(this);
                }
              }
            },
            formatter: function () {
              let result,
                  formatterContext = this,
                  containerClass = self.labelClickCallback ? 'label-container clickable' : 'label-container',
                  category = self.categories.find(cat => cat.name === formatterContext.value),
                  iconClass = category ? category.icon : undefined,
                  imgSrc = category && category.img ? baseUrl + category.img : undefined;

              result = self.labelClickCallback ? '<a class="add-label-click-callback">' : '';
              result += '<div class="' + containerClass + '" title="' + formatterContext.value + '"><div class="label-text">' + formatterContext.value + '</div>';

              if (iconClass) {
                result += '<i class="label-icon ' + iconClass + '"></i>';
              } else if (imgSrc) {
                result += '<img class="label-img" src="' + imgSrc + '" />';
              }

              result += '</div>'
              result += self.labelClickCallback ? '</a>' : '';

              return result;
            }
          }
        },
        yAxis: {
          gridLineWidth: 0, // this should disable grid lines, but it doesn't work, so we have to set a CSS rule instead
          labels: {
            enabled: false
          },
          title: {
            text: undefined
          }
        },
        credits: {
          enabled: false
        },
        series: self.chartSeries
      }

      function calculateHeight(numCategories) {
        if (self.chartSeries.length === 1) {
          if (numCategories < 3) {
            return 94;
          }
          // The goal is to have approximately 20px of space between each bar.
          // Adding 34px per category seems to be the best way to accomplish this.
          return 94 + ((numCategories - 2) * 34);
        }

        return MAX_HEIGHT;
      }
    }
  },
  mounted() {
    let self = this;
    // We need to wait a bit to call reflow here; $nextTick seems to be the cleanest option.
    this.$nextTick(() => { self.$refs.chartComponent.chart.reflow(); });
  },
  methods: {
    chartRendered: function () {
      let self = this;
      const labels = document.getElementsByClassName('add-label-click-callback');

      [...labels].forEach(label => {
          if (self.labelClickCallback) {
            label.removeEventListener('click', self.invokeLabelClickedCallback);
            label.addEventListener('click', self.invokeLabelClickedCallback);
          }
      });

    },
    invokeLabelClickedCallback: function(e) {
      let self = this;
      
      if (self.labelClickCallback) {
        self.labelClickCallback(e.currentTarget.text);
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.user-details-trend-chart.chart-container {
  max-height: 264px; // this should be MAX_HEIGHT + 10px
  overflow: auto;
}
</style>

<style lang="scss">
.user-details-trend-chart.chart-container {
  .highcharts-container {
    max-width: 100%;
    .highcharts-root {
      // max-width: 100%; // TODO: is this necessary?
      .highcharts-grid-line {
        stroke-width: 0;
      }
      .highcharts-point {
        // Have to set border radius this way so that it only applies to the end of the bar (the highcharts API borderRadius config sets it for the beginning as well)
        clip-path: inset(0% 0% -2px 0% round 2px);
      }
    }
  }
  .label-container {
    display: flex;
    &.clickable:hover {
      text-decoration: underline;
    }
    .label-text {
      display: inline-block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      padding-right: 6px;
      flex: 6;
    }
    .label-img {
      width: 13px;
      height: 13px;
    }
  }
}
</style>
